import { api } from 'store/api';
import { ApiResponse } from 'store/api/types';

import { TOKEN_ID_HEADER } from 'store/app/constants';
import { ActivityType, EntityStatus } from 'store/app/enums';
import { User } from 'store/auth/models';
import {
  EditOrganisationParams,
  EntityAccount,
  EntityGroupData,
  ExcelFileMetadata,
  ExcelTransaction,
  ExcelTransactions,
  LastReceiptUploadStatusData,
  Mapping,
  NewOrganisationParams,
  Organisation,
  OrganisationAttachment,
  ProviderUploadReceiptResponse,
  ReceiptStatisticsPayload,
  TrackingCategory,
  TransactionItem,
  UploadExcelTransactionsValidationData,
  UploadOrganisationAttachmentParams,
} from './models';
import { Contact } from 'store/vat-checker/models';
import { getHmrcHeader } from 'store/vat/hmrcUtils';
import type { ProviderPostStatus } from './types';

/* export interface FetchOrganisationsResponse extends ApiResponse {
  data: { organisations: Organisation[] };
}

export const getOrganisations = (): Promise<FetchOrganisationsResponse> =>
  api.get<FetchOrganisationsResponse>(`/auth/organisation/getAll`);
 */

export interface FetchOrganisationsResponse extends ApiResponse {
  data: { organisations: Organisation[]; mappings: Mapping[] };
}

export const getOrganisations = (
  currentUsers: User[],
  searchPrompt: string
): Promise<FetchOrganisationsResponse> => {
  const useAiSearch = process.env.USE_AI_SEARCH === 'true';
  if (useAiSearch) {
    return api.post<FetchOrganisationsResponse>(`/auth/returns/search`, {
      body: { currentUsers, searchPrompt },
    });
  }
  return api.get<FetchOrganisationsResponse>(`/auth/returns/ai/search`, {
    body: { currentUsers, searchPrompt },
  });
};

export interface FetchMappingsResponse extends ApiResponse {
  //data: { mappings: Mapping[] };
  data: { mappings: Mapping[] };
}

export const getMappings = (): Promise<FetchMappingsResponse> =>
  api.get<FetchMappingsResponse>(`/auth/returns/mapping/getAll`);

export const disconnectOrganisation = (
  organisationId: string,
  statusType?: EntityStatus
): Promise<ApiResponse> => {
  const url = statusType
    ? `/auth/organisation/disconnect/${organisationId}?statusType=${statusType}`
    : `/auth/organisation/disconnect/${organisationId}`;

  return api.post<ApiResponse>(url, {
    headers: {
      [TOKEN_ID_HEADER]: organisationId,
    },
  });
};

export const reactivateOrganisation = (
  organisationId: string
): Promise<ApiResponse> =>
  api.post<ApiResponse>(`/auth/organisation/reactivate/${organisationId}`, {
    headers: {
      [TOKEN_ID_HEADER]: organisationId,
    },
  });

export interface FetchAddUrlResponse extends ApiResponse {
  data: { url: string };
}

export const getAddOrganisationUrl = (
  providerName: string,
  uuid: string,
  sourceUrl: string
): Promise<FetchAddUrlResponse> =>
  api.get<FetchAddUrlResponse>(
    `/auth/organisation/connect/${providerName}/${uuid}?client=ACCOUNTING&sourceUrl=${sourceUrl}`
  );

export const connectCin7Entity = (
  key: string,
  cin7accountId: string,
  orgName: string
): Promise<ApiResponse> =>
  api.get<ApiResponse>(
    `/auth/organisation/connect/cin7/${cin7accountId}?key=${key}&cin7accountId=${cin7accountId}&orgName=${orgName}`
  );

export const editOrganisation = (
  editOrganisation: EditOrganisationParams
): Promise<ApiResponse> =>
  api.put<ApiResponse>('/auth/organisation/editconnection', {
    body: editOrganisation,
  });

export const addOrganisation = (
  addOrganisation: NewOrganisationParams
): Promise<ApiResponse> =>
  api.post<ApiResponse>('/auth/organisation/connect/excel', {
    body: addOrganisation,
  });

export interface UploadOrganisationAttachmentResponse extends ApiResponse {
  data: { attachment: OrganisationAttachment };
}
export const uploadOrganisationAttachment = (
  params: UploadOrganisationAttachmentParams
): Promise<UploadOrganisationAttachmentResponse> => {
  const formData = new FormData();
  formData.append('description', params.description);
  formData.append('file', params.file);
  return api.put<UploadOrganisationAttachmentResponse>(
    `/file/upload/connection/${params.organisationId}/attachment`,
    {
      body: formData,
      //headers: { 'Content-Type': 'multipart/form-data' },
      formData: true,
      headers: { [TOKEN_ID_HEADER]: params.organisationId },
    }
  );
};
export const deleteOrganisationAttachment = (
  organisationId: string,
  attachmentId: string
): Promise<ApiResponse> =>
  api.delete<ApiResponse>(
    `/file/delete/connection/${organisationId}/attachment/${attachmentId}/delete`,
    {
      headers: { [TOKEN_ID_HEADER]: organisationId },
    }
  );

export interface DownloadOrganisationAttachmentResponse extends ApiResponse {
  data: { file: string; fileName: string; description: string };
}
export const downloadOrganisationAttachment = (
  organisationId: string,
  attachmentId: string
): Promise<DownloadOrganisationAttachmentResponse> =>
  api.get<DownloadOrganisationAttachmentResponse>(
    `/file/download/connection/${organisationId}/attachment/${attachmentId}`,
    {
      headers: { [TOKEN_ID_HEADER]: organisationId },
    }
  );

export interface FetchAccountsResponse extends ApiResponse {
  data: { accounts: EntityAccount[] };
}

export const getAccounts = (
  connectionId: string,
  mappingId: string
): Promise<FetchAccountsResponse> =>
  api.get<FetchAccountsResponse>(
    `/auth/organisation/get/accounts/${connectionId}?mappingId=${mappingId}`,
    {
      headers: {
        [TOKEN_ID_HEADER]: connectionId,
      },
    }
  );

export interface UploadExcelTransactionsResponse
  extends Omit<ApiResponse, 'data'> {
  data: UploadExcelTransactionsValidationData;
}

export const uploadExcelTransactions = (
  organisationId: string,
  file: File,
  forceDuplicates: boolean,
  mapping: string
): Promise<UploadExcelTransactionsResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('forceDuplicates', String(forceDuplicates));
  formData.append('mapping', mapping);
  return api.post<UploadExcelTransactionsResponse>(
    `/auth/excel/connection/${organisationId}/upload`,
    {
      body: formData,
      formData: true,
      headers: { [TOKEN_ID_HEADER]: organisationId },
    }
  );
};

export interface UploadReceiptResponse extends ApiResponse {
  // The backend may return additional receipt import metadata in data; keep it generic.
  data: { batchId: string };
}

export const uploadReceipt = (
  organisationId: string,
  files: File[],
  grouped?: boolean
): Promise<UploadReceiptResponse> => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  const basePath = `/auth/receipt/${organisationId}/bulk/upload`;
  const url = grouped === true ? `${basePath}?grouped=true` : basePath;
  return api.post<UploadReceiptResponse>(url, {
    body: formData,
    formData: true,
    headers: { [TOKEN_ID_HEADER]: organisationId },
  });
};

export interface FetchExcelFilesMetadataResponse extends ApiResponse {
  data: { files: ExcelFileMetadata[] };
}
export const getExcelFilesMetadata = (
  organisationId: string
): Promise<FetchExcelFilesMetadataResponse> =>
  api.get<FetchExcelFilesMetadataResponse>(
    `/auth/excel/get/files/${organisationId}`,
    {
      headers: { [TOKEN_ID_HEADER]: organisationId },
    }
  );

export const deleteExcelTransactionsFile = (
  organisationId: string,
  importId: string
): Promise<ApiResponse> =>
  api.delete<ApiResponse>(
    `/auth/excel/delete/transactions/${importId}/${organisationId}`,
    {
      headers: { [TOKEN_ID_HEADER]: organisationId },
    }
  );

// Delete single Excel transaction (row-level delete in transactions tab)
export const deleteExcelTransaction = (
  organisationId: string,
  transactionUuid: string
): Promise<ApiResponse> =>
  api.delete<ApiResponse>(
    `/auth/excel/delete/transaction/${transactionUuid}/${organisationId}`,
    {
      headers: { [TOKEN_ID_HEADER]: organisationId },
    }
  );

/** Transaction edit/history timeline for a single Excel journal row. */
export const getTransactionHistory = (
  transactionUuid: string,
  applicationConnectionId: string
): Promise<ApiResponse> =>
  api.get<ApiResponse>(
    `/auth/excel/history/${transactionUuid}/${applicationConnectionId}`,
    {
      headers: { [TOKEN_ID_HEADER]: applicationConnectionId },
    }
  );
export interface UpdateExcelTransactionResponse extends ApiResponse {
  data: { newTransactionId?: string; validationErrors?: string[] };
}
// Update single Excel transaction
export const updateExcelTransaction = (
  organisationId: string,
  transactionUuid: string,
  body: ExcelTransaction
): Promise<UpdateExcelTransactionResponse> =>
  api.put<UpdateExcelTransactionResponse>(
    `/auth/excel/update/transaction/${transactionUuid}/${organisationId}`,
    { body, headers: { [TOKEN_ID_HEADER]: organisationId } }
  );

/**
 * Add manual Excel journal rows. Body is `List<ExcelJournalDTO>` — each item
 * matches the edit (`ExcelTransaction`) object shape. Backend places them under
 * a separate "Manual Entries" file.
 * Path param is applicationConnectionId on the API; FE passes organisationId.
 */
export const addExcelTransactions = (
  organisationId: string,
  body: ExcelTransaction[]
): Promise<ApiResponse> =>
  api.post<ApiResponse>(
    `/auth/excel/add/transactions/${organisationId}`,
    {
      body,
      headers: { [TOKEN_ID_HEADER]: organisationId },
    }
  );

/** Body for bulk update: apply the same field updates to multiple transaction IDs. */
export interface BulkUpdateExcelTransactionsBody {
  ids: string[];
  updates: Record<string, unknown>;
}

/** Update multiple Excel transactions with the same field changes (mass edit). */
export const bulkUpdateExcelTransactions = (
  organisationId: string,
  body: BulkUpdateExcelTransactionsBody
): Promise<ApiResponse> =>
  api.put<ApiResponse>(`/auth/excel/update/transactions/${organisationId}`, {
    body,
    headers: { [TOKEN_ID_HEADER]: organisationId },
  });

export interface FetchUploadedFileTransactionsResponse extends ApiResponse {
  data: { files: ExcelTransactions };
}
/** Convert 1-based UI page to 0-based API page (only at request time). */
//const toApiPage = (page: number | undefined | null): number =>
// page == null || page === 0 ? 0 : page - 1;

export const getUploadedFileTransactions = (
  organisationId: string,
  importId: string,
  page: number = 1,
  pageSize: number = 10
): Promise<FetchUploadedFileTransactionsResponse> =>
  api.get<FetchUploadedFileTransactionsResponse>(
    `/auth/excel/get/file/${importId}/transactions/${organisationId}`,
    {
      query: { page, pageSize },
      headers: { [TOKEN_ID_HEADER]: organisationId },
    }
  );

export interface FetchAllExcelTransactionsResponse extends ApiResponse {
  data: { transactions: ExcelTransactions };
}
export const getAllExcelTransactions = (
  organisationId: string,
  page: number = 1,
  pageSize: number = 10,
  filters?: Record<string, unknown>
): Promise<FetchAllExcelTransactionsResponse> => {
  const baseUrl = `/auth/excel/get/queried/transactions/${organisationId}`;

  // Align with VAT transactions pattern: page & pageSize in query string,
  // filters (including sort) in the request body when provided.
  const url = `${baseUrl}?page=${page}&pageSize=${pageSize}`;
  if (filters && Object.keys(filters).length > 0) {
    return api.post<FetchAllExcelTransactionsResponse>(url, {
      body: filters,
      headers: { [TOKEN_ID_HEADER]: organisationId },
    });
  }

  return api.post<FetchAllExcelTransactionsResponse>(baseUrl, {
    query: { page, pageSize },
    headers: { [TOKEN_ID_HEADER]: organisationId },
  });
};

export interface ExcelActivityLog {
  applicationConnectionId: string;
  accountDetailsId: string;
  userFullName: string;
  note: string;
  type: ActivityType;
  createdAt: string;
  importId: string;
  journalId: string;
  user: User;
}

export interface GetExcelActivityLogsResponse extends ApiResponse {
  data: ExcelActivityLog[];
}

export const getExcelActivityLogs = (
  organisationId: string
): Promise<GetExcelActivityLogsResponse> =>
  api.get<GetExcelActivityLogsResponse>(
    `/auth/excel/get/activity/${organisationId}`,
    {
      headers: { [TOKEN_ID_HEADER]: organisationId },
    }
  );

export interface GetTrackingCategoriesResponse extends ApiResponse {
  data: { trackingCategories: TrackingCategory[] };
}

export const getTrackingCategories = (
  organisationId: string
): Promise<GetTrackingCategoriesResponse> =>
  api.get<GetTrackingCategoriesResponse>(
    `/tracking/categories/${organisationId}`,
    {
      headers: { [TOKEN_ID_HEADER]: organisationId },
    }
  );

export interface GetTransactionItemsResponse extends ApiResponse {
  data: { data: TransactionItem[] };
}

export const getTransactionItems = (
  organisationId: string
): Promise<GetTransactionItemsResponse> =>
  api.get<GetTransactionItemsResponse>(`/items/${organisationId}`, {
    headers: { [TOKEN_ID_HEADER]: organisationId },
  });

// Add a custom note to the Excel activity log timeline for a given organisation.
export const addExcelActivityNote = (
  organisationId: string,
  note: string
): Promise<ApiResponse> =>
  api.get<ApiResponse>(`/auth/excel/add/note/activity/${organisationId}`, {
    query: { note },
    headers: { [TOKEN_ID_HEADER]: organisationId },
  });

export interface GetLastReceiptUploadStatusResponse extends ApiResponse {
  data: LastReceiptUploadStatusData;
}

export const getLastReceiptUploadStatus = (
  organisationId: string
): Promise<GetLastReceiptUploadStatusResponse> =>
  api.get<GetLastReceiptUploadStatusResponse>(
    `/auth/receipt/${organisationId}/upload/status`,
    {
      headers: { [TOKEN_ID_HEADER]: organisationId },
    }
  );

export const setTransactionsReviewed = (
  organisationId: string,
  reviewIds: string[]
): Promise<ApiResponse> =>
  api.post<ApiResponse>(`/auth/receipt/${organisationId}/review`, {
    body: { reviewIds },
    headers: { [TOKEN_ID_HEADER]: organisationId },
  });

export const registerDriveFolder = (
  folderId: string,
  connectionId: string,
  type: 'google' | 'one',
  driveId: string | null,
  syncExistingFiles: boolean
): Promise<ApiResponse> => {
  return api.post<any>(
    `/${type}/drive/register/folder?syncExistingFiles=${syncExistingFiles}`,
    {
      body: {
        folderId,
        connectionId,
        driveId,
      },
      headers: { [TOKEN_ID_HEADER]: connectionId },
    }
  );
};

export const saveGoogleToken = (code: string, connectionId: string) => {
  return api.post<any>(`/google/drive/exchange/code`, {
    body: {
      code,
      connectionId,
    },
    headers: { [TOKEN_ID_HEADER]: connectionId },
  });
};

export const getDriveScannerStatus = (connectionId: string) => {
  return api.get<any>(`/drive/scanner/status/${connectionId}`, {
    headers: { [TOKEN_ID_HEADER]: connectionId },
  });
};

export const getFilesUploadHistory = (connectionId: string, type: string) => {
  return api.get<any>(`/auth/excel/get/files/${connectionId}/type/${type}`, {
    headers: { [TOKEN_ID_HEADER]: connectionId },
  });
};

export interface UploadReceiptToProviderResponse extends ApiResponse {
  data: ProviderUploadReceiptResponse[];
}
export const uploadReceiptToProvider = (
  connectionId: string,
  filesIds: string[],
  status?: ProviderPostStatus,
  bankAccountId?: string | null
): Promise<UploadReceiptToProviderResponse> => {
  const validStatus =
    status === 'DRAFT' || status === 'SUBMITTED' || status === 'AUTHORISED'
      ? status
      : undefined;
  const validBankAccountId =
    typeof bankAccountId === 'string' && bankAccountId.trim()
      ? bankAccountId.trim()
      : undefined;
  const addStatus = validStatus ? `status=${validStatus}` : '';
  const addBankAccountId = validBankAccountId
    ? `${addStatus ? '&' : ''}bankAccountId=${encodeURIComponent(validBankAccountId)}`
    : '';
  const query = `${addStatus}${addBankAccountId}`;
  return api.post<UploadReceiptToProviderResponse>(
    `/auth/receipt/${connectionId}/files/provider/upload${
      query ? `?${query}` : ''
    }`,
    {
      body: filesIds,
      headers: { [TOKEN_ID_HEADER]: connectionId },
    }
  );
};

export const createEntityGroup = (
  groupData: EntityGroupData
): Promise<ApiResponse> =>
  api.post<ApiResponse>(`/auth/organisation/connect/group`, {
    body: groupData,
  });

export const updateEntityGroup = (
  groupId: string,
  groupData: EntityGroupData
): Promise<ApiResponse> =>
  api.put<ApiResponse>(`/auth/organisation/edit/group/${groupId}`, {
    body: groupData,
  });

export const deleteEntityGroup = (groupId: string): Promise<ApiResponse> =>
  api.delete<ApiResponse>(`/auth/organisation/delete/group/${groupId}`);

export const disconnectFolderRequest = (
  connectionId,
  type
): Promise<ApiResponse> =>
  api.delete<ApiResponse>(`/${type}/drive/disconnect/${connectionId}`, {
    headers: { [TOKEN_ID_HEADER]: connectionId },
  });

export const rescanReceipt = (
  organisationId: string,
  fileId: string,
  grouped?: boolean
): Promise<ApiResponse> => {
  const basePath = `/auth/receipt/${organisationId}/file/${fileId}/rescan`;
  let url = basePath;
  if (grouped === true) url = `${basePath}?grouped=true`;
  else if (grouped === false) url = `${basePath}?grouped=false`;
  return api.post<ApiResponse>(url, {
    headers: { [TOKEN_ID_HEADER]: organisationId },
  });
};

export interface GetOrganisationContactsResponse extends ApiResponse {
  data: { contacts: Contact[] };
}
export const getOrganisationContacts = (
  connectionId: string
): Promise<GetOrganisationContactsResponse> =>
  api.get<GetOrganisationContactsResponse>(`/contacts/${connectionId}`, {
    headers: {
      [TOKEN_ID_HEADER]: connectionId,
    },
  });

export const createExcelContact = (
  organisationId: string,
  excelContact: Contact
): Promise<ApiResponse> =>
  api.post<ApiResponse>(`/auth/excel/contact/${organisationId}`, {
    body: excelContact,
    headers: {
      [TOKEN_ID_HEADER]: organisationId,
    },
  });

export const updateExcelContact = (
  organisationId: string,
  contactUuid: string,
  excelContact: Contact
): Promise<ApiResponse> =>
  api.put<ApiResponse>(`/auth/excel/contact/${organisationId}/${contactUuid}`, {
    body: excelContact,
    headers: {
      [TOKEN_ID_HEADER]: organisationId,
    },
  });

export const deleteExcelContact = (
  organisationId: string,
  contactUuid: string
): Promise<ApiResponse> =>
  api.delete<ApiResponse>(
    `/auth/excel/contact/${organisationId}/${contactUuid}`,
    {
      headers: {
        [TOKEN_ID_HEADER]: organisationId,
      },
    }
  );

export const generateScanEmail = (
  connectionId: string
): Promise<ApiResponse> => {
  return api.post<ApiResponse>(`/email/scanner/${connectionId}/generate`);
};

export const getScanEmailStatus = (
  connectionId: string
): Promise<ApiResponse> => {
  return api.get<ApiResponse>(`/email/scanner/${connectionId}/status`);
};

export const completeScanSteps = (
  organisationId: string
): Promise<ApiResponse> => {
  return api.post<ApiResponse>(
    `/auth/organisation/${organisationId}/scan/steps/complete`
  );
};

export interface GetReceiptStatisticsResponse extends ApiResponse {
  data: {
    data?: ReceiptStatisticsPayload;
  } & ReceiptStatisticsPayload;
}

export type ReceiptStatisticsQuery = {
  from?: string;
  to?: string;
};

const buildReceiptStatisticsQuery = (
  query?: ReceiptStatisticsQuery
): Record<string, string> | undefined => {
  if (!query) return undefined;
  const entries: Record<string, string> = {};
  if (query.from) entries.from = query.from;
  if (query.to) entries.to = query.to;
  return Object.keys(entries).length > 0 ? entries : undefined;
};

export const getReceiptStatisticsAll = (
  query?: ReceiptStatisticsQuery
): Promise<GetReceiptStatisticsResponse> =>
  api.get<GetReceiptStatisticsResponse>('/auth/receipt/statistics', {
    query: buildReceiptStatisticsQuery(query),
  });

export const getReceiptStatisticsForEntity = (
  connectionId: string,
  query?: ReceiptStatisticsQuery
): Promise<GetReceiptStatisticsResponse> =>
  api.get<GetReceiptStatisticsResponse>(
    `/auth/receipt/${connectionId}/statistics`,
    {
      query: buildReceiptStatisticsQuery(query),
      headers: { [TOKEN_ID_HEADER]: connectionId },
    }
  );

export const HMRCFraudHeaderTest = (
  connectionId: string
): Promise<ApiResponse> => {
  const url = '/auth/organisation/HMRC/' + connectionId + '/test-fraud-header';

  return getHmrcHeader().then((hmrcHeader) =>
    api.post<ApiResponse>(url, {
      body: { hmrcHeader },
      headers: {
        [TOKEN_ID_HEADER]: connectionId,
      },
    })
  );
};
